import React, { useEffect, useMemo, useState } from "react";
import useUrlState from "../../hooks/useUrlState";
import useScrollRestoration from "../../hooks/useScrollRestoration";
import {
  Avatar,
  Box,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import { Search, Close, CheckCircle, Cancel, Add, Delete, Edit, Refresh } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import PhoneNumberField from "../../components/common/PhoneNumberField";
import { PERMISSION_GROUPS, PERMISSION_LABELS } from "../../constants/permissionConstants";
import userService from "../../services/userService";
import roleService from "../../services/roleService";
import { slugifyTab } from "../../utils/tabSlug";
// User account active/disabled isn't part of the DB-backed status system
// (it's a boolean flag, not an admin-configurable status flow), so it keeps
// its own small, fixed color mapping here rather than going through StatusChip.
const AccountStatusChip = ({ active }) => (
  <Chip
    label={active ? "Active" : "Disabled"}
    size="small"
    sx={{
      bgcolor: active ? "#C6F6D5" : "#FFF5F5",
      color: active ? "#22543D" : "#C53030",
      fontWeight: 600,
      fontSize: "0.6875rem",
      height: 22,
      border: "none",
      "& .MuiChip-label": { px: 1.25 },
    }}
  />
);



const TABS = [
  { label: "Customers", roleFilter: "customer" },
  { label: "Admins", roleFilter: "admin" },
  { label: "Riders", roleFilter: "rider" },
  { label: "User Roles" },
];

const PROTECTED_ROLE_NAMES = ["admin", "customer", "rider"];

const URL_SCHEMA = {
  // The tab's slugified label ("customers", "user_roles", …) rather than
  // its array index — stable if tabs are ever reordered, and readable in
  // the address bar/shared links instead of a meaningless number.
  tab: { default: "" },
  search: { default: "" },
  page: { default: 0, parse: (v) => parseInt(v, 10) || 0 },
  rowsPerPage: { default: 20, parse: (v) => parseInt(v, 10) || 20 },
};

const AdminCustomers = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  useScrollRestoration();
  const [urlState, setUrlState] = useUrlState(URL_SCHEMA);
  const { tab, page, rowsPerPage } = urlState;
  const activeTab = Math.max(0, TABS.findIndex((t) => slugifyTab(t.label) === tab));
  // Local typing buffer — see AdminShipments.jsx's identical comment for why
  // this isn't synced to the URL on every keystroke, only on Enter/commit.
  const [search, setSearch] = useState(urlState.search);
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [roleLoading, setRoleLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  // Bulk row selection — Customers tab only (COLS Agent authorization is a
  // customer-account concept). Scoped to whatever's currently on screen:
  // cleared on tab/page/search change rather than tracked across pages, so
  // "N selected" never silently includes rows the admin can't see anymore.
  const [bulkSelectedIds, setBulkSelectedIds] = useState(new Set());
  const [bulkSaving, setBulkSaving] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    password: "",
    role: "customer",
    isActive: true,
    isVerified: true,
  });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: "", description: "", permissions: [] });

  const roleOptions = useMemo(
    () => roles.map((role) => ({ name: role.name, label: role.name })),
    [roles],
  );

  const loadUsers = async (overrides = {}) => {
    setLoading(true);
    try {
      const { data } = await userService.getAll({
        role: TABS[activeTab].roleFilter,
        search: search || undefined,
        page: page + 1,
        limit: rowsPerPage,
        ...overrides,
      });
      setItems(data.data || []);
      setTotal(data.pagination?.total || 0);
      setBulkSelectedIds(new Set());
    } catch (err) {
      enqueueSnackbar("Failed to load users", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    setRoleLoading(true);
    try {
      const { data } = await roleService.getAll();
      setRoles(data.data.roles || []);
    } catch (err) {
      enqueueSnackbar("Failed to load roles", { variant: "error" });
    } finally {
      setRoleLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
  }, []);

  useEffect(() => {
    if (TABS[activeTab].roleFilter) {
      loadUsers();
    }
  }, [activeTab, page, rowsPerPage]);

  const handleSearch = (e) => {
    if (e.key === "Enter") {
      setUrlState({ search: e.target.value, page: 0 });
      loadUsers({ page: 1, search: e.target.value });
    }
  };

  const handleTabChange = (_, newValue) => {
    setUrlState({ tab: slugifyTab(TABS[newValue].label), page: 0, search: "" });
    setSearch("");
    setSelected(null);
    setEditing(false);
    setBulkSelectedIds(new Set());
  };

  const toggleSelectOne = (id) => {
    setBulkSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    setBulkSelectedIds((prev) => (prev.size === items.length ? new Set() : new Set(items.map((u) => u._id))));
  };

  const handleBulkSetAgent = async (isWaypointImportsAgent) => {
    if (bulkSelectedIds.size === 0) return;
    setBulkSaving(true);
    try {
      const { data } = await userService.bulkSetWaypointAgent({
        userIds: [...bulkSelectedIds],
        isWaypointImportsAgent,
      });
      enqueueSnackbar(data.message || "Accounts updated", { variant: "success" });
      loadUsers();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to update accounts", { variant: "error" });
    } finally {
      setBulkSaving(false);
    }
  };

  // "Bulk Mark by TIN" dialog — paste a list of TIN numbers (from COLS
  // records, say) instead of hunting each one down in the table. Reports
  // back which TINs didn't match any account, rather than silently
  // dropping them.
  const [tinBulkOpen, setTinBulkOpen] = useState(false);
  const [tinBulkInput, setTinBulkInput] = useState("");
  const [tinBulkSaving, setTinBulkSaving] = useState(false);

  const handleBulkSetAgentByTin = async () => {
    const tins = tinBulkInput
      .split(/[\n,]/)
      .map((t) => t.trim())
      .filter(Boolean);
    if (tins.length === 0) return;

    setTinBulkSaving(true);
    try {
      const { data } = await userService.bulkSetWaypointAgent({ tins, isWaypointImportsAgent: true });
      const notFound = data.data?.notFoundTins || [];
      enqueueSnackbar(data.message || "Accounts updated", { variant: "success" });
      if (notFound.length > 0) {
        enqueueSnackbar(`No account found for TIN${notFound.length === 1 ? "" : "s"}: ${notFound.join(", ")}`, {
          variant: "warning",
          autoHideDuration: 8000,
        });
      }
      setTinBulkOpen(false);
      setTinBulkInput("");
      loadUsers();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to update accounts", { variant: "error" });
    } finally {
      setTinBulkSaving(false);
    }
  };

  const openDetail = (item) => {
    setSelected(item);
    setEditForm({
      firstName: item.firstName || "",
      lastName: item.lastName || "",
      email: item.email || "",
      phone: item.phone || "",
      role: item.role || "customer",
      isActive: item.isActive,
      tin: item.tin || "",
      tins: item.tins || [],
      canManageMultipleTins: !!item.canManageMultipleTins,
      isWaypointImportsAgent: !!item.isWaypointImportsAgent,
      canBulkImportShipments: !!item.canBulkImportShipments,
      canBulkAddShipments: !!item.canBulkAddShipments,
      isHouseAccount: !!item.isHouseAccount,
    });
    setEditing(false);
    setDetailOpen(true);
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const saveUser = async ({ tinOverride, reassignRemovedTinsTo } = {}) => {
    if (!selected || !editForm) return;
    try {
      const payload = {
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        email: editForm.email,
        phone: editForm.phone,
        role: editForm.role,
        isActive: editForm.isActive,
        tin: tinOverride !== undefined ? tinOverride : editForm.tin,
        tins: editForm.tins,
        canManageMultipleTins: editForm.canManageMultipleTins,
        isWaypointImportsAgent: editForm.isWaypointImportsAgent,
        canBulkImportShipments: editForm.canBulkImportShipments,
        canBulkAddShipments: editForm.canBulkAddShipments,
        isHouseAccount: editForm.isHouseAccount,
      };
      if (reassignRemovedTinsTo) payload.reassignRemovedTinsTo = reassignRemovedTinsTo;
      await userService.update(selected._id, payload);
      enqueueSnackbar("User updated", { variant: "success" });
      setEditing(false);
      setDetailOpen(false);
      setReassignDialog(null);
      loadUsers();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to update user", {
        variant: "error",
      });
    }
  };

  // Every past shipment/consolidation carries its own TIN snapshot, not a
  // live reference — so before a save that would make a TIN disappear from
  // the account (unchecking multi-TIN entirely, or deleting one TIN chip
  // while staying multi-TIN), the admin has to say where that history goes.
  // Diffs `selected` (as loaded) against `editForm` (as edited) rather than
  // intercepting each checkbox/delete click, so it works the same way for
  // either path.
  const [reassignDialog, setReassignDialog] = useState(null);
  const [reassignChoice, setReassignChoice] = useState("");

  const handleSaveClick = () => {
    if (!selected || !editForm) return;

    const oldTins = selected.canManageMultipleTins
      ? selected.tins || []
      : selected.tin
        ? [{ tin: selected.tin }]
        : [];

    // Disabling multi-TIN management with more than one prior TIN on file:
    // there's no way to infer which one the admin wants to keep, so always
    // ask rather than silently defaulting to the first in the list.
    if (!editForm.canManageMultipleTins && selected.canManageMultipleTins && oldTins.length > 1) {
      setReassignChoice(oldTins[0].tin);
      setReassignDialog({ mode: "disable", removedFrom: oldTins, survivors: oldTins });
      return;
    }

    const newTins = editForm.canManageMultipleTins
      ? (editForm.tins || []).filter((t) => t.tin)
      : editForm.tin
        ? [{ tin: editForm.tin }]
        : [];
    const removedTins = oldTins.filter((o) => !newTins.some((n) => n.tin === o.tin));

    if (removedTins.length === 0) {
      saveUser();
      return;
    }
    if (newTins.length === 0) {
      enqueueSnackbar(
        "At least one TIN must remain — add one back, or uncheck “Can manage multiple TINs” instead.",
        { variant: "error" },
      );
      return;
    }
    setReassignChoice(newTins[0].tin);
    setReassignDialog({ mode: "removeChip", removedFrom: removedTins, survivors: newTins });
  };

  const confirmReassign = () => {
    if (!reassignDialog || !reassignChoice) return;
    if (reassignDialog.mode === "disable") {
      saveUser({ tinOverride: reassignChoice, reassignRemovedTinsTo: reassignChoice });
    } else {
      saveUser({ reassignRemovedTinsTo: reassignChoice });
    }
  };

  const toggleStatus = async (item) => {
    try {
      await userService.update(item._id, { isActive: !item.isActive });
      enqueueSnackbar(
        `${item.firstName} ${item.lastName} has been ${item.isActive ? "disabled" : "enabled"}`,
        { variant: "success" },
      );
      if (activeTab < 2) {
        loadUsers();
      }
    } catch {
      enqueueSnackbar("Failed to update status", { variant: "error" });
    }
  };

  const openCreateDialog = () => {
    setCreateForm({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      role: TABS[activeTab].roleFilter || "customer",
      isActive: true,
      isVerified: true,
    });
    setCreateOpen(true);
  };

  const handleCreateChange = (field, value) => {
    setCreateForm((prev) => ({ ...prev, [field]: value }));
  };


  const openRoleDialog = (role) => {
    if (role) {
      setEditingRole(role);
      setRoleForm({
        name: role.name,
        description: role.description || "",
        permissions: role.permissions || [],
      });
    } else {
      setEditingRole(null);
      setRoleForm({ name: "", description: "", permissions: [] });
    }
    setRoleDialogOpen(true);
  };

  const handleRolePermissionToggle = (permission) => {
    setRoleForm((prev) => {
      const hasPermission = prev.permissions.includes(permission);
      return {
        ...prev,
        permissions: hasPermission
          ? prev.permissions.filter((item) => item !== permission)
          : [...prev.permissions, permission],
      };
    });
  };

  const saveRole = async () => {
    try {
      if (!roleForm.name) {
        enqueueSnackbar("Role name is required", { variant: "warning" });
        return;
      }
      if (editingRole) {
        await roleService.update(editingRole._id, roleForm);
        enqueueSnackbar("Role updated", { variant: "success" });
      } else {
        await roleService.create(roleForm);
        enqueueSnackbar("Role created", { variant: "success" });
      }
      setRoleDialogOpen(false);
      loadRoles();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to save role", {
        variant: "error",
      });
    }
  };

  const deleteRole = async (roleId) => {
    try {
      await roleService.delete(roleId);
      enqueueSnackbar("Role deleted", { variant: "success" });
      loadRoles();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to delete role", {
        variant: "error",
      });
    }
  };

  // Permanent, unlike Disable Account (which just flips isActive and keeps
  // everything intact/reversible) — this actually removes the user
  // document. Used both from the table's row action and, with the same
  // confirm dialog, from a pending deletion-request review.
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await userService.delete(deleteTarget._id);
      enqueueSnackbar(`${deleteTarget.firstName} ${deleteTarget.lastName}'s account was deleted`, {
        variant: "success",
      });
      setDeleteTarget(null);
      setDetailOpen(false);
      loadUsers();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to delete account", { variant: "error" });
    } finally {
      setDeleting(false);
    }
  };

  const createPasswordValid = createForm.password.length >= 8;

  const createUser = async () => {
    if (!createPasswordValid) {
      enqueueSnackbar("Password must be at least 8 characters", { variant: "warning" });
      return;
    }
    try {
      await userService.create(createForm);
      enqueueSnackbar("User created successfully", { variant: "success" });
      setCreateOpen(false);
      loadUsers();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.message || "Failed to create user", {
        variant: "error",
      });
    }
  };

  const isCustomersTab = TABS[activeTab].roleFilter === "customer";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader title="Users Management" subtitle="Manage customers, administrators, and role permissions." />

      <Card>
        <Box
          sx={{
            p: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": {
                fontSize: 13,
                textTransform: "none",
                fontWeight: 600,
                minHeight: 44,
              },
            }}
          >
            {TABS.map((t) => (
              <Tab key={t.label} label={t.label} />
            ))}
          </Tabs>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {TABS[activeTab].roleFilter && (
              <TextField
                size="small"
                placeholder="Search name, email…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleSearch}
                sx={{ width: 260 }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ fontSize: 16, color: "text.disabled" }} />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            )}
            {isCustomersTab && (
              <Button size="small" variant="outlined" onClick={() => setTinBulkOpen(true)}>
                Bulk Mark by TIN
              </Button>
            )}
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={() => (TABS[activeTab].roleFilter ? loadUsers() : loadRoles())}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button
              startIcon={<Add />}
              variant="contained"
              onClick={() => (TABS[activeTab].roleFilter ? openCreateDialog() : openRoleDialog())}
            >
              {TABS[activeTab].roleFilter ? "New User" : "New Role"}
            </Button>
          </Box>
        </Box>

        {TABS[activeTab].roleFilter ? (
          loading ? (
            <SkeletonLoader rows={Math.min(8, rowsPerPage)} />
          ) : items.length === 0 ? (
            <EmptyState
              icon="👤"
              title={`No ${TABS[activeTab].label.toLowerCase()} found`}
              description="Try adjusting your filters or create a new account."
              actionLabel="Add user"
              onAction={openCreateDialog}
            />
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              {isCustomersTab && bulkSelectedIds.size > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 2,
                    py: 1.25,
                    bgcolor: "primary.50",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="body2" fontWeight={600}>
                    {bulkSelectedIds.size} selected
                  </Typography>
                  <Button
                    size="small"
                    variant="contained"
                    disabled={bulkSaving}
                    onClick={() => handleBulkSetAgent(true)}
                  >
                    Assigned Waypoint Imports as Agent
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={bulkSaving}
                    onClick={() => handleBulkSetAgent(false)}
                  >
                    Remove Agent Status
                  </Button>
                  <Button size="small" onClick={() => setBulkSelectedIds(new Set())} disabled={bulkSaving}>
                    Clear
                  </Button>
                </Box>
              )}
              <Table>
                <TableHead>
                  <TableRow>
                    {isCustomersTab && (
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          checked={items.length > 0 && bulkSelectedIds.size === items.length}
                          indeterminate={bulkSelectedIds.size > 0 && bulkSelectedIds.size < items.length}
                          onChange={toggleSelectAllVisible}
                        />
                      </TableCell>
                    )}
                    <TableCell>User</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                    <TableCell>TIN</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Verified</TableCell>
                    <TableCell>Joined</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((user) => (
                    <TableRow
                      key={user._id}
                      hover
                      selected={bulkSelectedIds.has(user._id)}
                      onClick={() => navigate(`/admin/customers/${user._id}`)}
                      sx={{ cursor: "pointer" }}
                    >
                      {isCustomersTab && (
                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            size="small"
                            checked={bulkSelectedIds.has(user._id)}
                            onChange={() => toggleSelectOne(user._id)}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          <Avatar
                            src={user.profilePhotoUrl}
                            sx={{ width: 30, height: 30, bgcolor: "primary.main", fontSize: 11, fontWeight: 700 }}
                          >
                            {user.firstName?.[0]}
                            {user.lastName?.[0]}
                          </Avatar>
                          <Typography variant="body2" fontWeight={600}>
                            {user.firstName} {user.lastName}
                          </Typography>
                          {user.isWaypointImportsAgent && (
                            <Tooltip title="Authorized Waypoint Imports as Agent (COLS)">
                              <Chip label="Agent" size="small" color="primary" sx={{ fontSize: "0.65rem", height: 20 }} />
                            </Tooltip>
                          )}
                          {user.deletionRequestedAt && (
                            <Tooltip title="Requested account deletion">
                              <Chip label="Deletion requested" size="small" color="warning" sx={{ fontSize: "0.65rem", height: 20 }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary", fontSize: 13 }}>{user.email}</TableCell>
                      <TableCell sx={{ color: "text.secondary", fontSize: 13 }}>{user.phone || "—"}</TableCell>
                      <TableCell sx={{ color: "text.secondary", fontSize: 13 }}>
                        {user.role === "customer"
                          ? user.tins?.length > 0
                            ? user.tins.map((t) => t.tin).join(", ")
                            : user.tin || "—"
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <AccountStatusChip active={user.isActive} />
                      </TableCell>
                      <TableCell>
                        {user.isVerified ? (
                          <CheckCircle sx={{ fontSize: 16, color: "success.main" }} />
                        ) : (
                          <Cancel sx={{ fontSize: 16, color: "text.disabled" }} />
                        )}
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary", fontSize: 12 }}>
                        {dayjs(user.createdAt).format("MMM D, YYYY h:mm A")}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <Tooltip title="Quick edit">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDetail(user);
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete account">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteTarget(user);
                              }}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )
        ) : roleLoading ? (
          <SkeletonLoader rows={6} />
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            {roles.length === 0 ? (
              <EmptyState
                icon="🔐"
                title="No roles found"
                description="Create custom roles to assign permissions across your team."
                actionLabel="New role"
                onAction={() => openRoleDialog()}
              />
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Role</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Permissions</TableCell>
                    <TableCell />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {roles.map((role) => (
                    <TableRow key={role._id} hover>
                      <TableCell sx={{ textTransform: "capitalize" }}>{role.name}</TableCell>
                      <TableCell sx={{ color: "text.secondary", fontSize: 13 }}>{role.description || "—"}</TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          {role.permissions?.map((permission) => (
                            <Chip key={permission} label={permission} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Edit />}
                            onClick={() => openRoleDialog(role)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            startIcon={<Delete />}
                            onClick={() => deleteRole(role._id)}
                          >
                            Delete
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        )}

        {TABS[activeTab].roleFilter && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setUrlState({ page: p })}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => setUrlState({ rowsPerPage: +e.target.value, page: 0 })}
            rowsPerPageOptions={[10, 20, 50]}
            sx={{ borderTop: "1px solid", borderColor: "divider" }}
          />
        )}
      </Card>

      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        {selected && (
          <>
            <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Avatar src={selected.profilePhotoUrl} sx={{ bgcolor: "primary.main", fontWeight: 700 }}>
                  {selected.firstName?.[0]}
                  {selected.lastName?.[0]}
                </Avatar>
                <Box>
                  <Typography fontWeight={700}>{selected.firstName} {selected.lastName}</Typography>
                  <Typography variant="caption" color="text.secondary">{selected.email}</Typography>
                </Box>
              </Box>
              <IconButton size="small" onClick={() => { setDetailOpen(false); setEditing(false); }}>
                <Close />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {!editing ? (
                [
                  { label: "Phone", value: selected.phone || "—" },
                  { label: "Role", value: selected.role || "—" },
                  {
                    label: "Multi-TIN Management",
                    value: selected.canManageMultipleTins ? "Enabled" : "Disabled",
                  },
                  // TIN vs. TINs list are mutually exclusive: a customer
                  // either has one account-wide TIN, or manages several via
                  // the list — never both, since enabling the flag folds the
                  // old primary TIN into the list and clears it.
                  ...(selected.canManageMultipleTins
                    ? [
                        {
                          label: "TINs",
                          value:
                            selected.tins && selected.tins.length > 0
                              ? selected.tins
                                  .map((t) => (t.label ? `${t.tin} (${t.label})` : t.tin))
                                  .join(", ")
                              : "—",
                        },
                      ]
                    : [{ label: "TIN", value: selected.tin || "—" }]),
                  {
                    label: "Waypoint Imports Agent",
                    value: selected.isWaypointImportsAgent ? "Yes" : "No",
                  },
                  {
                    label: "Bulk Shipment Import (CSV)",
                    value: selected.canBulkImportShipments ? "Enabled" : "Disabled",
                  },
                  {
                    label: "Bulk Add Shipments (Manual Entry)",
                    value: selected.canBulkAddShipments ? "Enabled" : "Disabled",
                  },
                  {
                    label: "House Account",
                    value: selected.isHouseAccount ? "Yes — invoices auto-settle" : "No",
                  },
                  {
                    label: "Account Status",
                    value: <AccountStatusChip active={selected.isActive} />,
                  },
                  { label: "Email Verified", value: selected.isVerified ? "Yes" : "No" },
                  { label: "Joined", value: dayjs(selected.createdAt).format("MMMM D, YYYY") },
                  {
                    label: "Last Login",
                    value: selected.lastLogin ? dayjs(selected.lastLogin).format("MMM D, YYYY h:mm A") : "Never",
                  },
                ].map((row) => (
                  <Box
                    key={row.label}
                    sx={{ display: "flex", justifyContent: "space-between", py: 1.25, borderBottom: "1px solid", borderColor: "divider", '&:last-child': { borderBottom: 'none' } }}
                  >
                    <Typography variant="body2" color="text.secondary">{row.label}</Typography>
                    <Typography variant="body2" fontWeight={600}>{row.value}</Typography>
                  </Box>
                ))
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    label="First name"
                    value={editForm?.firstName || ""}
                    onChange={(e) => handleEditChange("firstName", e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Last name"
                    value={editForm?.lastName || ""}
                    onChange={(e) => handleEditChange("lastName", e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Email address"
                    type="email"
                    value={editForm?.email || ""}
                    onChange={(e) => handleEditChange("email", e.target.value)}
                    fullWidth
                  />
                  <PhoneNumberField
                    label="Phone"
                    value={editForm?.phone || ""}
                    onChange={(phone) => handleEditChange("phone", phone)}
                    fullWidth
                  />
                  <FormControl fullWidth>
                    <InputLabel>Role</InputLabel>
                    <Select
                      value={editForm?.role || "customer"}
                      label="Role"
                      onChange={(e) => handleEditChange("role", e.target.value)}
                    >
                      {roleOptions.map((option) => (
                        <MenuItem key={option.name} value={option.name}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {!editForm?.canManageMultipleTins && (
                    <TextField
                      label="TIN"
                      value={editForm?.tin || ""}
                      onChange={(e) => handleEditChange("tin", e.target.value)}
                      fullWidth
                    />
                  )}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!editForm?.canManageMultipleTins}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setEditForm((prev) => {
                            const next = { ...prev, canManageMultipleTins: checked };
                            // Enabling multi-TIN for a customer who already has a
                            // primary TIN: fold it into the list as an explicit
                            // entry instead of leaving it as an implied default.
                            // (The server does this too on save, regardless of
                            // what's shown here — this just previews it.)
                            if (
                              checked &&
                              prev.tin &&
                              !(prev.tins || []).some((t) => t.tin === prev.tin)
                            ) {
                              next.tins = [{ tin: prev.tin, label: "Primary" }, ...(prev.tins || [])];
                            }
                            // No more single "account TIN" once multi-TIN
                            // management is on — matches the server clearing
                            // user.tin on save.
                            if (checked) next.tin = "";
                            // Revoking: restore a single account TIN from the
                            // list and clear it out — matches the server.
                            if (!checked && (prev.tins || []).length > 0) {
                              next.tin = prev.tin || prev.tins[0].tin;
                              next.tins = [];
                            }
                            return next;
                          });
                        }}
                      />
                    }
                    label="Can manage multiple TINs"
                  />
                  {editForm?.canManageMultipleTins && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Additional TINs
                      </Typography>
                      {(editForm?.tins || []).map((row, idx) => (
                        <Box key={idx} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                          <TextField
                            size="small"
                            label="TIN"
                            value={row.tin || ""}
                            onChange={(e) => {
                              const next = [...(editForm.tins || [])];
                              next[idx] = { ...next[idx], tin: e.target.value };
                              handleEditChange("tins", next);
                            }}
                          />
                          <TextField
                            size="small"
                            label="Label (optional)"
                            value={row.label || ""}
                            onChange={(e) => {
                              const next = [...(editForm.tins || [])];
                              next[idx] = { ...next[idx], label: e.target.value };
                              handleEditChange("tins", next);
                            }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => {
                              const next = (editForm.tins || []).filter((_, i) => i !== idx);
                              handleEditChange("tins", next);
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}
                      <Button
                        size="small"
                        startIcon={<Add />}
                        onClick={() =>
                          handleEditChange("tins", [...(editForm.tins || []), { tin: "", label: "" }])
                        }
                        sx={{ alignSelf: "flex-start" }}
                      >
                        Add TIN
                      </Button>
                    </Box>
                  )}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!editForm?.isWaypointImportsAgent}
                        onChange={(e) =>
                          handleEditChange("isWaypointImportsAgent", e.target.checked)
                        }
                      />
                    }
                    label="Assigned Waypoint Imports as Agent"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!editForm?.canBulkImportShipments}
                        onChange={(e) =>
                          handleEditChange("canBulkImportShipments", e.target.checked)
                        }
                      />
                    }
                    label="Can bulk-import shipments via CSV"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!editForm?.canBulkAddShipments}
                        onChange={(e) =>
                          handleEditChange("canBulkAddShipments", e.target.checked)
                        }
                      />
                    }
                    label="Can bulk-add shipments (manual entry form)"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={!!editForm?.isHouseAccount}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          if (
                            checked &&
                            !window.confirm(
                              "Turning on House Account will immediately mark every current invoice for this customer as paid, and every invoice created for them from now on will auto-settle the same way. Their revenue is excluded from the main Profit & Loss report and shown separately under the \"House Accounts\" tab instead. Continue?",
                            )
                          ) {
                            return;
                          }
                          handleEditChange("isHouseAccount", checked);
                        }}
                      />
                    }
                    label="House Account (auto-settle invoices, exclude from P&L)"
                  />
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              {!editing ? (
                <>
                  <Button size="small" variant="outlined" onClick={() => setEditing(true)}>
                    Edit Account
                  </Button>
                  <Button
                    variant={selected.isActive ? "outlined" : "contained"}
                    color={selected.isActive ? "error" : "success"}
                    onClick={() => {
                      toggleStatus(selected);
                      setDetailOpen(false);
                    }}
                  >
                    {selected.isActive ? "Disable Account" : "Enable Account"}
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    onClick={() => setDeleteTarget(selected)}
                  >
                    Delete Account
                  </Button>
                  <Button variant="contained" onClick={() => { setDetailOpen(false); setEditing(false); }}>
                    Close
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setEditing(false);
                      setEditForm({
                        firstName: selected.firstName || "",
                        lastName: selected.lastName || "",
                        email: selected.email || "",
                        phone: selected.phone || "",
                        role: selected.role || "customer",
                        isActive: selected.isActive,
                        tin: selected.tin || "",
                        tins: selected.tins || [],
                        canManageMultipleTins: !!selected.canManageMultipleTins,
                        isWaypointImportsAgent: !!selected.isWaypointImportsAgent,
                        canBulkImportShipments: !!selected.canBulkImportShipments,
                        canBulkAddShipments: !!selected.canBulkAddShipments,
                        isHouseAccount: !!selected.isHouseAccount,
                      });
                    }}
                  >
                    Cancel
                  </Button>
                  <Button variant="contained" onClick={handleSaveClick}>
                    Save
                  </Button>
                </>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      <Dialog
        open={!!reassignDialog}
        onClose={() => setReassignDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {reassignDialog?.mode === "disable" ? "Choose which TIN to keep" : "Reassign removed TIN(s)"}
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {reassignDialog?.mode === "disable"
              ? "This customer has more than one TIN on file. Turning off multi-TIN management collapses them down to one — pick which TIN stays on the account. Every existing shipment and consolidation filed under the others will be moved to it."
              : `TIN(s) ${reassignDialog?.removedFrom.map((t) => t.tin).join(", ")} ${reassignDialog?.removedFrom.length === 1 ? "is" : "are"} being removed from this account. Every existing shipment and consolidation filed under ${reassignDialog?.removedFrom.length === 1 ? "it" : "them"} will move to whichever TIN you pick below.`}
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Keep / reassign to</InputLabel>
            <Select
              value={reassignChoice}
              label="Keep / reassign to"
              onChange={(e) => setReassignChoice(e.target.value)}
            >
              {(reassignDialog?.survivors || []).map((t) => (
                <MenuItem key={t.tin} value={t.tin}>
                  {t.label ? `${t.tin} (${t.label})` : t.tin}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="outlined" onClick={() => setReassignDialog(null)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={confirmReassign} disabled={!reassignChoice}>
            Confirm &amp; Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete this account?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            This permanently deletes{" "}
            <strong>
              {deleteTarget?.firstName} {deleteTarget?.lastName}
            </strong>
            's account ({deleteTarget?.email}). This cannot be undone — if they might need this account again,
            use Disable Account instead.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="outlined" onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={confirmDelete} disabled={deleting}>
            {deleting ? "Deleting…" : "Delete Account"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={tinBulkOpen}
        onClose={() => !tinBulkSaving && setTinBulkOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bulk Mark by TIN</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Paste TIN numbers — one per line, or comma-separated. Every matching customer account (checked against
            both their primary TIN and any additional TINs on multi-TIN accounts) will be marked as having
            authorized Waypoint Imports as Agent.
          </Typography>
          <TextField
            autoFocus
            multiline
            minRows={8}
            fullWidth
            placeholder={"e.g.\n123456789\n987654321\n456789123"}
            value={tinBulkInput}
            onChange={(e) => setTinBulkInput(e.target.value)}
            disabled={tinBulkSaving}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="outlined" onClick={() => setTinBulkOpen(false)} disabled={tinBulkSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleBulkSetAgentByTin}
            disabled={tinBulkSaving || !tinBulkInput.trim()}
          >
            {tinBulkSaving ? "Updating…" : "OK"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
            <TextField
              label="First name"
              name="new-user-first-name"
              value={createForm.firstName}
              onChange={(e) => handleCreateChange("firstName", e.target.value)}
              fullWidth
              autoComplete="off"
            />
            <TextField
              label="Last name"
              name="new-user-last-name"
              value={createForm.lastName}
              onChange={(e) => handleCreateChange("lastName", e.target.value)}
              fullWidth
              autoComplete="off"
            />
            <TextField
              label="Email address"
              name="new-user-email"
              type="email"
              value={createForm.email}
              onChange={(e) => handleCreateChange("email", e.target.value)}
              fullWidth
              autoComplete="off"
            />
            <PhoneNumberField
              label="Phone"
              value={createForm.phone}
              onChange={(phone) => handleCreateChange("phone", phone)}
              fullWidth
            />
            <TextField
              label="Password"
              name="new-user-password"
              type="password"
              value={createForm.password}
              onChange={(e) => handleCreateChange("password", e.target.value)}
              fullWidth
              autoComplete="new-password"
              error={createForm.password.length > 0 && !createPasswordValid}
              helperText={
                createForm.password.length > 0 && !createPasswordValid
                  ? "Must be at least 8 characters"
                  : "Min. 8 characters"
              }
              slotProps={{
                input: {
                  endAdornment: createForm.password.length > 0 && (
                    <InputAdornment position="end">
                      {createPasswordValid ? (
                        <CheckCircle sx={{ fontSize: 18, color: "success.main" }} />
                      ) : (
                        <Cancel sx={{ fontSize: 18, color: "error.main" }} />
                      )}
                    </InputAdornment>
                  ),
                },
              }}
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={createForm.role}
                label="Role"
                onChange={(e) => handleCreateChange("role", e.target.value)}
              >
                {roleOptions.map((option) => (
                  <MenuItem key={option.name} value={option.name}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="outlined" onClick={() => setCreateOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={createUser}
            disabled={
              !createForm.firstName ||
              !createForm.lastName ||
              !createForm.email ||
              !createPasswordValid
            }
          >
            Create User
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={roleDialogOpen} onClose={() => setRoleDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingRole ? "Edit Role" : "Create New Role"}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
            <TextField
              label="Role name"
              value={roleForm.name}
              onChange={(e) => setRoleForm((prev) => ({ ...prev, name: e.target.value.toLowerCase() }))}
              fullWidth
              disabled={Boolean(editingRole && PROTECTED_ROLE_NAMES.includes(editingRole.name))} />
            <TextField
              label="Description"
              value={roleForm.description}
              onChange={(e) => setRoleForm((prev) => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                Permissions
              </Typography>
              <Box sx={{ maxHeight: 360, overflowY: "auto", pr: 1 }}>
                {PERMISSION_GROUPS.map((group) => (
                  <Box key={group.label} sx={{ mb: 2 }}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={700}
                      display="block"
                      sx={{ textTransform: "uppercase", letterSpacing: "0.05em", mb: 0.5 }}
                    >
                      {group.label}
                    </Typography>
                    <FormGroup>
                      {group.permissions.map((key) => (
                        <FormControlLabel
                          key={key}
                          control={
                            <Checkbox
                              size="small"
                              checked={roleForm.permissions.includes(key)}
                              onChange={() => handleRolePermissionToggle(key)}
                            />
                          }
                          label={PERMISSION_LABELS[key] || key}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button variant="outlined" onClick={() => setRoleDialogOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveRole}>
            {editingRole ? "Save Role" : "Create Role"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminCustomers;
